/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct, RatingsSummary } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { AttributeValue, ProductDimensionValueInventoryAvailability, ProductPrice, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    AttributesForSelectedVariantInput,
    FullProductInput,
    getAttributesForSelectedVariantAction,
    getFullProductsAction,
    getRatingsSummary,
    GetRatingsSummaryInput,
    ProductDetailsCriteria,
    getDimensionAvailabilitiesForProductsAction,
    ProductsDimensionAvailabilitiesInput,
    GetCustomerInput,
    getCustomer
} from '@msdyn365-commerce-modules/retail-actions';

import { ProductComparisonItem, ProductComparisonState } from '../../product-comparison-state';

/**
 * Represents the specifications of the product.
 */
export interface IProductSpecification {
    productId: number;
    catalogId: number;
    specificationsList: AttributeValue[];
}

/**
 * Represents the dimension of single product.
 */
export interface IDimensionResult {
    result: ProductDimensionValueInventoryAvailability[][];
}

/**
 * Represents the dimension of the product.
 */
export interface IProductDimensionAvailibility {
    dimensions: IDimensionResult[];
    recordId: number;
    masterId: number;
}
/**
 * Represents the data for a single product comparison item.
 */
export interface IProductComparisonItemData {
    itemId: string;
    productInfo: SimpleProduct;
    catalogId: number;
    price?: ProductPrice;
    rating?: RatingsSummary;
    specifications?: IProductSpecification;
    dimensions?: IProductDimensionAvailibility;
}

/**
 * Input class for GetProductsInProductComparisonInput data action.
 */
export class GetProductsInProductComparisonInput implements IActionInput {
    public readonly items?: ProductComparisonItem[];

    public constructor(items?: ProductComparisonItem[]) {
        this.items = items;
    }

    /**
     * Cache key.
     * @returns Cache key.
     */
    public getCacheKey = (): string => 'GetProductsInProductComparisonInput';

    /**
     * Cache object type.
     * @returns Cache object type.
     */
    public getCacheObjectType = (): string => 'GetProductsInProductComparison';

    /**
     * Data cache type.
     * @returns Data cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * Creates input for the data action.
 * @param inputData - Action context info.
 * @returns Action input.
 */
export const createGetProductsInProductComparisonInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): GetProductsInProductComparisonInput => {
    return new GetProductsInProductComparisonInput();
};

/**
 * Loads full product info for each comparison item.
 * @param input - Data action input.
 * @param actionContext - Action context.
 * @returns Full product info.
 */
const loadFullProducts = async (input: GetProductsInProductComparisonInput, actionContext: IActionContext) => {
    const fullProductInput = input.items!.map(
        item =>
            new FullProductInput(
                item.productId,
                actionContext.requestContext.apiSettings,
                new ProductDetailsCriteria(true, true),
                actionContext.requestContext,
                item.catalogId
            )
    );

    let productsInfo = await getFullProductsAction(fullProductInput, actionContext);
    productsInfo = ArrayExtensions.validValues(productsInfo);

    return productsInfo;
};

/**
 * Loads ratings for each comparison item.
 * @param input - Data action input.
 * @param actionContext - Action context.
 * @returns Ratings info.
 */
const loadRatings = async (input: GetProductsInProductComparisonInput, actionContext: IActionContext) => {
    const apiSettings = actionContext.requestContext.apiSettings;
    const ratingInputs = input.items!.map(
        item => new GetRatingsSummaryInput(item.productId.toString(), apiSettings.rnr.id || '', apiSettings.rnr.url || '')
    );

    const ratingsPromises = ratingInputs.map(ratingInput => getRatingsSummary(ratingInput, actionContext));
    let ratings = await Promise.all(ratingsPromises);
    ratings = ArrayExtensions.validValues(ratings);

    return ratings;
};

/**
 * Loads product specification for the given product.
 * @param item - Input item.
 * @param product - Product for specification.
 * @param actionContext - Action context.
 * @returns Product specifications.
 */
const loadProductSpecifications = async (item: ProductComparisonItem, product: FullProduct, actionContext: IActionContext) => {
    const apiSettings = actionContext.requestContext.apiSettings;
    const input = new AttributesForSelectedVariantInput(
        product.ProductDetails.RecordId,
        apiSettings.channelId,
        product.ProductDetails,
        item.catalogId
    );
    const specificationsList = await getAttributesForSelectedVariantAction(input, actionContext);

    const productSpecification: IProductSpecification = {
        productId: product.ProductDetails.RecordId,
        specificationsList,
        catalogId: item.catalogId
    };
    return productSpecification;
};

/**
 * Loads product specification for each comparison item.
 * @param input - Action input.
 * @param products - Products for specification.
 * @param actionContext - Action context.
 * @returns Products specifications info.
 */
const loadProductsSpecifications = async (
    input: GetProductsInProductComparisonInput,
    products: FullProduct[],
    actionContext: IActionContext
) => {
    const productsSpecificationsPromises = products.map(async (product, index) => {
        const item = input.items![index];
        return loadProductSpecifications(item, product, actionContext);
    });

    let productsSpecifications = await Promise.all(productsSpecificationsPromises);
    productsSpecifications = ArrayExtensions.validValues(productsSpecifications);

    return productsSpecifications;
};

/**
 * Loads product dimension for each comparison item.
 * @param products - Products for specification.
 * @param actionContext - Action context.
 * @returns Products specifications info.
 */
const loadProductsDimensions = async (products: FullProduct[], actionContext: IActionContext) => {
    const productsDimensionPromises = products.map(async product => {
        const dimensionResult: IDimensionResult[] = [];

        for (const item of product.ProductDetails.Dimensions!) {
            const input = new ProductsDimensionAvailabilitiesInput(
                product.ProductDetails.MasterProductId ?? product.ProductDetails.RecordId,
                item.DimensionTypeValue,
                actionContext.requestContext
            );
            await getDimensionAvailabilitiesForProductsAction([input], actionContext).then(result => {
                dimensionResult.push({ result });
            });
        }

        product.ProductDetails.Dimensions?.map(async item => {
            return dimensionResult;
        });

        const getUpdatedDimension: IProductDimensionAvailibility | undefined =
            dimensionResult.length > 0
                ? {
                      dimensions: dimensionResult,
                      recordId: product.ProductDetails.RecordId,
                      masterId: product.ProductDetails.MasterProductId!
                  }
                : undefined;
        return getUpdatedDimension;
    });

    let productsDimensions = await Promise.all(productsDimensionPromises);
    productsDimensions = ArrayExtensions.validValues(productsDimensions);
    return productsDimensions;
};

/**
 * Retrieves product data for the items in the comparison.
 * @param input - Action input.
 * @param actionContext - Action context.
 * @returns Products data.
 */
export const getProductsInProductComparisonAction = async (
    input: GetProductsInProductComparisonInput,
    actionContext: IActionContext
): Promise<IProductComparisonItemData[]> => {
    const catalogId = getCatalogId(actionContext.requestContext);

    let items = input.items;
    if (!ArrayExtensions.hasElements(items)) {
        const accountInformation = await getCustomer(new GetCustomerInput(actionContext.requestContext.apiSettings), actionContext);
        items = ProductComparisonState.instance(actionContext.requestContext, accountInformation).items;
    }

    const newInput: GetProductsInProductComparisonInput = {
        items: items.filter(item => item.catalogId === catalogId),
        getCacheKey: input.getCacheKey,
        getCacheObjectType: input.getCacheObjectType,
        dataCacheType: input.dataCacheType
    };

    if (!ArrayExtensions.hasElements(newInput.items)) {
        return [];
    }

    const productsPromises = loadFullProducts(newInput, actionContext);
    const ratingsPromises = loadRatings(newInput, actionContext);
    const [products, ratings] = await Promise.all([productsPromises, ratingsPromises]);
    const productDimensions = await loadProductsDimensions(products, actionContext);
    const productsSpecifications = await loadProductsSpecifications(newInput, products, actionContext);

    const productsData = newInput.items.map((item, index) => {
        const product = products[index];
        const productData: IProductComparisonItemData = {
            itemId: item.itemId,
            productInfo: product.ProductDetails,
            price: product.ProductPrice,
            catalogId: item.catalogId
        };
        productData.rating = ratings.find(rating => rating.productId === productData.productInfo.RecordId.toString());
        productData.specifications = productsSpecifications.find(
            productSpecifications =>
                productSpecifications.productId === productData.productInfo.RecordId &&
                productSpecifications.catalogId === productData.catalogId
        );
        productData.dimensions = productDimensions.find(dimension => dimension?.recordId === productData.productInfo.RecordId);
        return productData;
    });
    return productsData;
};

/**
 * Data action to retrieve products info for the items in the comparison.
 */
export const getActiveCheckoutCartProductsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/product-comparison/product-comparison/get-products-in-product-comparison',
    action: getProductsInProductComparisonAction as IAction<IProductComparisonItemData[]>,
    input: createGetProductsInProductComparisonInput
});

export default getActiveCheckoutCartProductsActionDataAction;
